import React, {useEffect, useMemo, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {
    Autocomplete,
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Modal,
    Pagination,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CodeIcon from "@mui/icons-material/Code";
import {useTranslation} from "react-i18next";

import {useAlert} from "../context/AlertContext";
import {useAuth} from "../context/AuthContext";
import BreadcrumbLink from "../components/BreadcrumbLink";
import AppTableHeaderCell from "../components/AppTableHeaderCell";
import AppModalBox from "../components/AppModalBox";

import {getTeacherCodeTaskAttempts, getMyStreams} from "../api/api";
import {AdminCodeTaskAttemptsResponse, CodeAttemptDetailsResponse} from "../types";

type StreamOption = {
    id: number;
    name: string;
};

const formatDateTime = (iso?: string, locale?: string) => {
    if (!iso) return "—";

    const d = new Date(iso);

    return isNaN(d.getTime()) ? iso : d.toLocaleString(locale);
};

const getInitials = (fullName?: string) => {
    if (!fullName || !fullName.trim()) return "?";

    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const TeacherCodeTaskAttemptsPage = () => {
    const {t, i18n} = useTranslation();

    const {taskId} = useParams<{ taskId?: string }>();
    const safeTaskId = taskId ? Number(taskId) : null;

    const {showAlert} = useAlert();
    const {hasRole} = useAuth();
    const isTeacher = hasRole?.("ROLE_TEACHER") ?? false;

    const [loading, setLoading] = useState(false);

    const [meta, setMeta] = useState<AdminCodeTaskAttemptsResponse | null>(null);

    const [attempts, setAttempts] = useState<CodeAttemptDetailsResponse[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [searchInput, setSearchInput] = useState("");
    const [searchEmail, setSearchEmail] = useState("");
    const [streamsLoading, setStreamsLoading] = useState(false);
    const [streams, setStreams] = useState<StreamOption[]>([]);
    const [selectedStreamIds, setSelectedStreamIds] = useState<number[]>([]);

    const selectedStreams = useMemo(() => {
        if (!streams.length || !selectedStreamIds.length) return [];

        const set = new Set(selectedStreamIds);

        return streams.filter((s) => set.has(s.id));
    }, [streams, selectedStreamIds]);

    const [codeOpen, setCodeOpen] = useState(false);
    const [selectedAttempt, setSelectedAttempt] =
        useState<CodeAttemptDetailsResponse | null>(null);

    const openCode = (a: CodeAttemptDetailsResponse) => {
        setSelectedAttempt(a);
        setCodeOpen(true);
    };

    const closeCode = () => {
        setCodeOpen(false);
        setSelectedAttempt(null);
    };

    const pageSize = 10;

    const loadStreams = async () => {
        try {
            setStreamsLoading(true);

            const res = await getMyStreams(0, 1000, "");

            setStreams(res.streams ?? []);
        } catch (e: any) {
            showAlert(e?.message ?? t("teachercodetaskattempts.errors.loadStreams"), "error");
            setStreams([]);
        } finally {
            setStreamsLoading(false);
        }
    };

    const loadAttempts = async () => {
        if (!safeTaskId) return;

        try {
            setLoading(true);

            const res: AdminCodeTaskAttemptsResponse = await getTeacherCodeTaskAttempts(
                safeTaskId,
                page - 1,
                pageSize,
                searchEmail,
                selectedStreamIds
            );

            setMeta(res);
            setAttempts(res?.attempts?.content ?? []);
            setTotalPages(res?.attempts?.page?.totalPages ?? 1);
        } catch (e: any) {
            showAlert(e?.message ?? t("teachercodetaskattempts.errors.loadAttempts"), "error");
            setMeta(null);
            setAttempts([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isTeacher) return;

        loadStreams();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTeacher]);

    useEffect(() => {
        if (!isTeacher) return;

        loadAttempts();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTeacher, safeTaskId, page, searchEmail, selectedStreamIds]);

    useEffect(() => {
        setPage(1);
    }, [safeTaskId, searchEmail, selectedStreamIds]);

    if (!isTeacher) {
        return (
            <Container sx={{mt: 4}}>
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        p: 3,
                        backgroundColor: theme.palette.background.paper,
                    })}
                >
                    <Typography sx={{fontSize: 18, fontWeight: 800}}>
                        {t("teachercodetaskattempts.accessDeniedTitle")}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                        {t("teachercodetaskattempts.accessDeniedDescription")}
                    </Typography>

                    <Box sx={{mt: 2}}>
                        <Button component={Link} to="/" variant="outlined">
                            {t("common.toMainPage")}
                        </Button>
                    </Box>
                </Paper>
            </Container>
        );
    }

    if (!safeTaskId) {
        return (
            <Container sx={{mt: 4}}>
                <Button component={Link} to="/" variant="outlined">
                    {t("common.toMainPage")}
                </Button>
            </Container>
        );
    }

    return (
        <Box sx={{px: {xs: 3, md: 3}}}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                maxItems={3}
                itemsBeforeCollapse={1}
                itemsAfterCollapse={4}
                sx={{
                    mb: 2,
                    "& .MuiBreadcrumbs-ol": {
                        overflow: "hidden",
                    },
                    "& .MuiBreadcrumbs-li": {
                        minWidth: 0,
                    },
                }}
            >
                <BreadcrumbLink to="/my-courses">
                    {t("breadcrumb.myCourses")}
                </BreadcrumbLink>

                {meta?.courseId ? (
                    <BreadcrumbLink to={`/course-details/${meta.courseId}`}>
                        {meta.courseTitle || t("breadcrumb.courseDetails")}
                    </BreadcrumbLink>
                ) : null}

                {meta?.chapterId ? (
                    <BreadcrumbLink to={`/chapter-details/${meta.chapterId}`}>
                        {meta.chapterTitle || t("breadcrumb.chapterDetails")}
                    </BreadcrumbLink>
                ) : null}

                {meta?.lessonId ? (
                    <BreadcrumbLink to={`/lesson-details/${meta.lessonId}`}>
                        {meta.lessonTitle || t("breadcrumb.lessonDetails")}
                    </BreadcrumbLink>
                ) : null}

                {meta?.codeTaskId ? (
                    <BreadcrumbLink to={`/lesson-details/${meta.lessonId}/tasks`}>
                        {t("tabs.codeTasks")}
                    </BreadcrumbLink>
                ) : null}

                {meta?.codeTaskId ? (
                    <BreadcrumbLink to={`/task-details/${meta.codeTaskId}`}>
                        {t("teachercodetaskattempts.details")}
                    </BreadcrumbLink>
                ) : null}

                <Typography color="text.primary" variant="body2" sx={{fontWeight: 600}}>
                    {t("teachercodetaskattempts.attempts")}
                </Typography>
            </Breadcrumbs>

            <Box sx={{mb: 2}}>
                <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                    {meta?.codeTaskTitle ?? t("teachercodetaskattempts.attempts")}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    {meta?.lessonTitle ? `${meta.lessonTitle} • ` : ""}
                    {meta?.chapterTitle ?? ""}
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.paper,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: {xs: 2, md: 3},
                            py: 2,
                            flexWrap: "wrap",
                            backgroundColor: theme.palette.background.paper,
                            borderBottom: `1px solid ${theme.palette.action.hover}`,
                        })}
                    >
                        <TextField
                            type="search"
                            size="small"
                            placeholder={t("teachercodetaskattempts.searchByEmail")}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setSearchEmail(searchInput.trim());
                                }
                            }}
                            sx={{width: {xs: "100%", sm: 320}}}
                        />

                        <IconButton
                            onClick={() => setSearchEmail(searchInput.trim())}
                            disabled={loading}
                            sx={(theme) => ({
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                "&:hover": {
                                    backgroundColor: theme.palette.action.selected,
                                },
                            })}
                            aria-label={t("common.search")}
                        >
                            <SearchIcon fontSize="small"/>
                        </IconButton>

                        <Autocomplete
                            multiple
                            size="small"
                            options={streams}
                            loading={streamsLoading}
                            value={selectedStreams}
                            getOptionLabel={(o) => o.name}
                            onChange={(_, value) => setSelectedStreamIds(value.map((v) => v.id))}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            sx={{minWidth: {xs: "100%", md: 360}}}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t("teachercodetaskattempts.filterByStreams")}
                                />
                            )}
                        />

                        <Button
                            variant="outlined"
                            size="small"
                            disabled={loading}
                            onClick={() => {
                                setSearchInput("");
                                setSearchEmail("");
                                setSelectedStreamIds([]);
                            }}
                            sx={{height: 40}}
                        >
                            {t("teachercodetaskattempts.clear")}
                        </Button>

                        <Box sx={{flexGrow: 1}}/>

                        <Typography variant="body2" color="text.secondary">
                            {t("teachercodetaskattempts.pageOf", {
                                page,
                                totalPages,
                            })}
                        </Typography>
                    </Box>

                    {attempts.length === 0 ? (
                        <Box sx={{py: 10, px: 3, textAlign: "center"}}>
                            <Typography variant="h6" sx={{mb: 1}}>
                                {t("teachercodetaskattempts.noAttemptsFound")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("teachercodetaskattempts.noAttemptsDescription")}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{width: "100%", overflowX: "auto"}}>
                            <table style={{width: "100%", borderCollapse: "separate", borderSpacing: 0}}>
                                <thead>
                                <tr style={{background: "#fafafa"}}>
                                    <AppTableHeaderCell>
                                        {t("teachercodetaskattempts.student")}
                                    </AppTableHeaderCell>
                                    <AppTableHeaderCell>
                                        {t("teachercodetaskattempts.email")}
                                    </AppTableHeaderCell>
                                    <AppTableHeaderCell>
                                        {t("teachercodetaskattempts.attemptTime")}
                                    </AppTableHeaderCell>
                                    <AppTableHeaderCell>
                                        {t("teachercodetaskattempts.attemptCount")}
                                    </AppTableHeaderCell>
                                    <AppTableHeaderCell align="center">
                                        {t("common.actions")}
                                    </AppTableHeaderCell>
                                    <AppTableHeaderCell>
                                        {t("teachercodetaskattempts.result")}
                                    </AppTableHeaderCell>
                                </tr>
                                </thead>

                                <tbody>
                                {attempts.map((a) => (
                                    <tr
                                        key={a.id}
                                        style={{
                                            transition: "background-color 0.2s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "#fafafa";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                        }}
                                    >
                                        <td style={{padding: "18px 16px"}}>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                <Avatar
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        bgcolor: "grey.300",
                                                        color: "text.primary",
                                                    }}
                                                >
                                                    {getInitials(a.userFullName)}
                                                </Avatar>

                                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                                    {a.userFullName || "—"}
                                                </Typography>
                                            </Box>
                                        </td>

                                        <td style={{padding: "18px 16px"}}>
                                            <Typography variant="body2">
                                                {a.userEmail || "—"}
                                            </Typography>
                                        </td>

                                        <td style={{padding: "18px 16px"}}>
                                            <Typography variant="body2">
                                                {formatDateTime(a.attemptTime, i18n.language)}
                                            </Typography>
                                        </td>

                                        <td style={{padding: "18px 16px"}}>
                                            <Typography variant="body2">
                                                {a.attemptAmount ?? 0}
                                            </Typography>
                                        </td>

                                        <td style={{padding: "18px 16px", textAlign: "center"}}>
                                            <Box sx={{display: "inline-flex", gap: 1}}>
                                                <Tooltip title={t("teachercodetaskattempts.viewCode")}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openCode(a)}
                                                        aria-label={t("teachercodetaskattempts.viewCode")}
                                                        sx={(theme) => ({
                                                            width: 36,
                                                            height: 36,
                                                            borderRadius: "50%",
                                                            backgroundColor: theme.palette.action.hover,
                                                            color: theme.palette.text.primary,
                                                            "&:hover": {
                                                                backgroundColor: theme.palette.action.selected,
                                                            },
                                                        })}
                                                    >
                                                        <CodeIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </td>

                                        <td style={{padding: "18px 16px"}}>
                                            <Chip
                                                size="small"
                                                label={
                                                    a.attemptResult
                                                        ? t("teachercodetaskattempts.solved")
                                                        : t("teachercodetaskattempts.notSolved")
                                                }
                                                color={a.attemptResult ? "success" : "error"}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </Box>
                    )}
                </Paper>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    disabled={loading}
                />
            </Box>

            <Modal open={codeOpen} onClose={closeCode}>
                <AppModalBox
                    sx={{
                        p: 4,
                        margin: "auto",
                        width: 900,
                        maxWidth: "95vw",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            borderRadius: 3,
                            overflow: "hidden",
                            backgroundColor: theme.palette.background.paper,
                        })}
                    >
                        <Box
                            sx={(theme) => ({
                                px: 3,
                                py: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                borderBottom: `1px solid ${theme.palette.action.hover}`,
                                backgroundColor: theme.palette.background.paper,
                            })}
                        >
                            <Box sx={{minWidth: 0}}>
                                <Box display="flex" alignItems="center" gap={1} sx={{minWidth: 0, mb: 0.5}}>
                                    <Typography sx={{fontSize: 18, fontWeight: 800}} noWrap>
                                        {t("teachercodetaskattempts.submittedCode")}
                                    </Typography>

                                    <Chip
                                        size="small"
                                        label={
                                            selectedAttempt?.attemptResult
                                                ? t("teachercodetaskattempts.solved")
                                                : t("teachercodetaskattempts.notSolved")
                                        }
                                        color={selectedAttempt?.attemptResult ? "success" : "error"}
                                        sx={{fontWeight: 700}}
                                    />
                                </Box>

                                <Typography variant="body2" color="text.secondary">
                                    <strong>{selectedAttempt?.userFullName || "—"}</strong>
                                    {selectedAttempt?.userEmail ? ` • ${selectedAttempt.userEmail}` : ""}
                                </Typography>

                                <Typography variant="caption" color="text.secondary">
                                    {t("teachercodetaskattempts.attemptTime")}:{" "}
                                    {formatDateTime(selectedAttempt?.attemptTime, i18n.language)}
                                </Typography>
                            </Box>

                            <Button onClick={closeCode} variant="outlined">
                                {t("common.close")}
                            </Button>
                        </Box>

                        <Box sx={{p: 3}}>
                            <Paper
                                elevation={0}
                                sx={(theme) => ({
                                    borderRadius: 2,
                                    p: 2,
                                    maxHeight: "70vh",
                                    overflow: "auto",
                                    backgroundColor:
                                        theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.04)"
                                            : "rgba(0,0,0,0.03)",
                                })}
                            >
                                <pre
                                    style={{
                                        margin: 0,
                                        fontFamily:
                                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                        fontSize: 13,
                                        lineHeight: 1.45,
                                        whiteSpace: "pre",
                                        overflowX: "auto",
                                    }}
                                >
                                    <code>
                                        {selectedAttempt?.codeSolution || t("teachercodetaskattempts.noCodeSubmitted")}
                                    </code>
                                </pre>
                            </Paper>
                        </Box>
                    </Paper>
                </AppModalBox>
            </Modal>
        </Box>
    );
};

export default TeacherCodeTaskAttemptsPage;